import { EtiquetaDePagina, CLASE_TITULO_DE_PAGINA } from '@/components/patterns';
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AppLayout } from '@/components/layout/app-layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  ArrowLeft,
  Building2,
  Users,
  FileText,
  Layers,
  Package,
  CheckCircle,
  Ban,
  CreditCard,
  LogIn,
  UserPlus,
  Trash2,
  RotateCcw,
} from 'lucide-react';
import { TenantModulesPanel } from '../components/tenant-modules-panel';
import { TenantPermissionsPanel } from '../components/tenant-permissions-panel';
import { TenantTiendasPrixiaPanel } from '../components/tenant-tiendas-prixia-panel';
import { RubroYTiendaDeLaPrueba } from '../components/rubro-y-tienda-de-la-prueba';
import { TEXTOS_DE_LA_FICHA, TEXTOS_DE_LA_CONVERSION } from '../lib/demo-textos';
import { ConvertirDemoDialog } from '../components/convertir-demo-dialog';
import { PlazoDeLaPrueba } from '../components/plazo-de-la-prueba';
import { NuevoUsuarioDialog } from '../components/nuevo-usuario-dialog';
import { TopeDePersonas } from '../components/tope-de-personas';
import { EliminarEmpresaDialog } from '../components/eliminar-empresa-dialog';
import { AvisoDeRestauracion, type PersonaSinRestaurar } from '../components/aviso-de-restauracion';
import { ReiniciarDemoDialog } from '../components/reiniciar-demo-dialog';
import { adminService } from '../services/admin-service';
import { RubroDeLaEmpresa } from '../components/rubro-de-la-empresa';
import { textoDelDestino } from '../lib/destino-de-soporte';
import type { DestinoDeSoporte, TipoDeEmpresa } from '../types/admin.types';
import { SelectorDePlan } from '../components/selector-de-plan';
import { useAdminStore } from '../store/admin-store';
import { useAuthStore } from '@/features/auth/store/auth-store';
import { useConfirmDialog } from '@/components/ui/confirm-dialog';
import { RowActionsMenu } from '@/components/ui/row-actions-menu';
import { useToast } from '@/lib/hooks/use-toast';

const ROLE_LABELS: Record<string, string> = {
  PLATFORM_ADMIN: 'Super Admin',
  COMPANY_ADMIN: 'Administrador',
  SALESPERSON: 'Vendedor',
  APPROVER: 'Aprobador',
};

const SUB_STATUS_LABELS: Record<
  string,
  { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }
> = {
  ACTIVE: { label: 'Activa', variant: 'default' },
  EXPIRED: { label: 'Expirada', variant: 'destructive' },
  SUSPENDED: { label: 'Suspendida', variant: 'secondary' },
  CANCELLED: { label: 'Cancelada', variant: 'outline' },
};

export function AdminTenantDetailPage() {
  // Las acciones que cortan accesos preguntan antes. Ver confirm-dialog.tsx.
  const { confirm, ConfirmDialog } = useConfirmDialog();
  const { toast } = useToast();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [guardandoRubro, setGuardandoRubro] = useState(false);
  /** El tope que se está escribiendo; `null` = sin tocar (se muestra el guardado). */
  const [topeDemo, setTopeDemo] = useState<string | null>(null);
  const [reiniciandoDemo, setReiniciandoDemo] = useState(false);
  const { plans, fetchPlans, createSubscription } = useAdminStore();
  const impersonate = useAuthStore((s) => s.impersonate);
  const [nuevoUsuario, setNuevoUsuario] = useState(false);
  const [eliminandoEmpresa, setEliminandoEmpresa] = useState(false);
  const [convirtiendo, setConvirtiendo] = useState(false);
  // Quién no volvió a tener acceso en la última restauración (se muestra bajo el encabezado).
  const [sinRestaurar, setSinRestaurar] = useState<PersonaSinRestaurar[]>([]);
  const [tenant, setTenant] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [assignDialog, setAssignDialog] = useState(false);
  // Cambia cuando se cambia el rubro o la tienda de una demo: «Tiendas disponibles» se vuelve a leer.
  const [versionDeTiendas, setVersionDeTiendas] = useState(0);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    if (id) {
      setLoading(true);
      adminService
        .getTenantDetail(id)
        .then(setTenant)
        .finally(() => setLoading(false));
      fetchPlans();
    }
  }, [id, fetchPlans]);

  /* Volver a pedir el detalle. Lo usa el alta de usuario para verse reflejada. */
  const cargarDetalle = async () => {
    if (!id) return;
    setTenant(await adminService.getTenantDetail(id));
  };

  const handleAssignPlan = async () => {
    if (!id || !selectedPlan || !endDate) return;
    await createSubscription({
      tenantId: id,
      planId: selectedPlan,
      endDate: new Date(endDate).toISOString(),
    });
    setAssignDialog(false);
    await recargarEmpresa();
  };

  const handleImpersonate = async (userId: string) => {
    try {
      const slug = await impersonate(userId);
      /*
        Recarga completa y con la empresa en la dirección.

        🔴 `navigate()` no sirve acá por dos razones que se suman: el
        router se montó con el basename del plano en el que estábamos
        —ninguno, si venimos del panel— así que el destino saldría sin
        empresa; y el basename sólo se vuelve a resolver en un arranque
        de la app. Una URL sin empresa deja a la persona trabajando en
        direcciones que no dicen en cuál está, que es justo lo que este
        frente vino a eliminar.
      */
      window.location.href = slug ? `/${slug}/dashboard` : '/dashboard';
    } catch (err) {
      console.error('Error al impersonar:', err);
    }
  };

  /*
    El cambio de rubro se guarda solo, sin botón de confirmar: es un campo con dos
    opciones y reversible en un clic. Pedir confirmación para algo que no destruye nada
    entrena a la gente a apretar 'Sí' sin leer, y después no lee la que sí importa —la
    de desactivar la empresa, que está tres centímetros más a la derecha.
  */
  const cambiarRubro = async (nuevo: TipoDeEmpresa) => {
    if (!id || !tenant || nuevo === tenant.type) return;
    setGuardandoRubro(true);
    try {
      await adminService.updateTenant(id, { type: nuevo });
      const updated = await adminService.getTenantDetail(id);
      setTenant(updated);
    } catch (err) {
      console.error('No se pudo cambiar el rubro:', err);
    } finally {
      setGuardandoRubro(false);
    }
  };

  /* Mini Prixia (decisión 5): el tope de productos propios de esta demo. */
  const guardarTopeDemo = async () => {
    if (!id || topeDemo === null) return;
    const n = Number(topeDemo);
    const vacio = topeDemo.trim() === '';
    if (!vacio && (!Number.isInteger(n) || n < 0)) {
      toast({
        variant: 'destructive',
        title: 'El tope tiene que ser un número entero, 0 o más.',
      });
      return;
    }
    try {
      await adminService.updateTenant(id, { topeCatalogoDemo: vacio ? null : n });
      setTenant(await adminService.getTenantDetail(id));
      setTopeDemo(null);
      toast({ title: 'Tope guardado.' });
    } catch (err: any) {
      toast({
        variant: 'destructive',
        title: 'No se pudo guardar el tope',
        description: err?.response?.data?.error ?? 'Probá de nuevo en un momento.',
      });
    }
  };

  const aplicarToggleActive = async () => {
    if (!id || !tenant) return;
    await adminService.updateTenant(id, { isActive: !tenant.isActive });
    const updated = await adminService.getTenantDetail(id);
    setTenant(updated);
  };

  const handleToggleActive = () => {
    if (!tenant) return;

    /*
      Reactivar no pregunta: no le quita nada a nadie. Desactivar sí, porque deja
      a TODA la empresa afuera de un solo clic — es la acción más destructiva de
      esta pantalla y hasta ahora se ejecutaba sin preguntar.
    */
    if (tenant.isActive) {
      confirm({
        title: `¿Desactivar ${tenant.name}?`,
        description:
          'Todos sus usuarios van a quedar sin acceso al sistema. Se puede volver a activar después.',
        confirmText: 'Sí, desactivar',
        variant: 'destructive',
        onConfirm: aplicarToggleActive,
      });
      return;
    }
    void aplicarToggleActive();
  };

  /*
    Recorrido 3-nov: «Módulos incluidos» lee sus datos al montarse. Cuando cambia el plan
    (convertir, asignar, extender) se vuelve a leer: si no, mostraba el plan viejo hasta
    recargar la página.
  */
  const [versionDeModulos, setVersionDeModulos] = useState(0);
  const recargarEmpresa = async () => {
    if (!id) return;
    setTenant(await adminService.getTenantDetail(id));
    setVersionDeModulos((v) => v + 1);
  };

  const handleArchivar = () => {
    if (!tenant) return;
    confirm({
      title: `¿Archivar ${tenant.name}?`,
      description:
        'Nadie de la empresa va a poder entrar y no se va a leer ni programar nada para ella. No se borra ningún dato: se puede restaurar cuando quieras.',
      confirmText: 'Sí, archivar',
      variant: 'destructive',
      onConfirm: async () => {
        try {
          await adminService.archivarTenant(tenant.id);
          await recargarEmpresa();
          toast({ title: `${tenant.name} quedó archivada.` });
        } catch (err: any) {
          toast({
            title: 'No se pudo archivar',
            description: err?.response?.data?.error?.message ?? err?.message,
            variant: 'destructive',
          });
        }
      },
    });
  };

  const handleRestaurar = async () => {
    if (!tenant) return;
    try {
      const r = await adminService.restaurarTenant(tenant.id);
      setSinRestaurar(r?.sinRestaurar ?? []);
      await recargarEmpresa();
      toast({
        title: `${tenant.name} volvió a estar en uso.`,
        ...(r?.sinRestaurar?.length
          ? { description: `${r.sinRestaurar.length} no volvieron a tener acceso: mirá el aviso.` }
          : {}),
      });
    } catch (err: any) {
      toast({
        title: 'No se pudo restaurar',
        description: err?.response?.data?.error?.message ?? err?.message,
        variant: 'destructive',
      });
    }
  };

  const handleImpersonateConfirmado = async (userId: string, nombre: string) => {
    /*
      Impersonar es sensible: se entra al sistema COMO esa persona y todo lo que
      se haga queda a su nombre en la interfaz (en la auditoría sí queda quién
      fue de verdad). Vale preguntar antes.
    */
    // A3: se anuncia la empresa real: puede no ser ésta, si acá ya no tiene acceso.
    let destino: DestinoDeSoporte;
    try {
      destino = await adminService.destinoDeSoporte(userId);
    } catch (err: any) {
      toast({
        variant: 'destructive',
        title: 'No se puede entrar',
        description: err?.response?.data?.error ?? 'Ocurrió un error.',
      });
      return;
    }
    confirm({
      title: `¿Entrar como ${nombre}?`,
      description: `Vas a ver el sistema como lo ve esa persona, en ${textoDelDestino(destino)}. Queda registrado que fuiste vos. Se sale con "Volver a mi cuenta".`,
      confirmText: 'Entrar',
      onConfirm: () => handleImpersonate(userId),
    });
  };

  const handleEliminarUsuario = (userId: string, nombre: string) => {
    // L5 (1.3): eliminar libera el correo y deja el historial. Se dice en la pregunta.

    confirm({
      title: `¿Eliminar a ${nombre}?`,
      description:
        'Deja de poder entrar a todas sus empresas y sale de los listados. Su historial queda. Su correo queda libre para una cuenta nueva. Se puede restaurar desde Personas › Personas eliminadas mientras nadie use ese correo.',
      confirmText: 'Sí, eliminar',
      variant: 'destructive',
      onConfirm: async () => {
        try {
          await adminService.deleteUser(userId);
          toast({ title: `${nombre} fue eliminado.` });
          await cargarDetalle();
        } catch (err: any) {
          toast({
            variant: 'destructive',
            title: 'No se pudo eliminar',
            description: err?.response?.data?.error ?? 'Ocurrió un error.',
          });
        }
      },
    });
  };

  if (loading) {
    return (
      <AppLayout variante="plataforma">
        <div className="p-6 space-y-4">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-48" />
          <Skeleton className="h-64" />
        </div>
      </AppLayout>
    );
  }

  if (!tenant) {
    return (
      <AppLayout variante="plataforma">
        <div className="p-6">
          <p className="text-muted-foreground">Empresa no encontrada</p>
        </div>
      </AppLayout>
    );
  }

  const activeSub = tenant.subscriptions?.find(
    (s: any) => s.status === 'ACTIVE' && new Date(s.endDate) >= new Date()
  );

  return (
    <AppLayout variante="plataforma">
      <ConfirmDialog />
      {id && (
        <ConvertirDemoDialog
          abierto={convirtiendo}
          empresaId={id}
          nombre={tenant.name}
          onCerrar={() => setConvirtiendo(false)}
          onConvertida={() => void recargarEmpresa()}
          planes={plans}
        />
      )}
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/plataforma/empresas')}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Volver
          </Button>
          <div className="flex-1">
            <div className="mb-3">
              <EtiquetaDePagina tono="neutro">Plataforma · Empresa</EtiquetaDePagina>
            </div>
            <h1 className={CLASE_TITULO_DE_PAGINA}>
              <Building2 className="h-6 w-6" />
              {tenant.name}
            </h1>
            <p className="text-sm text-muted-foreground">
              {tenant.slug} | {tenant.email}
            </p>
            {Array.isArray(tenant.parecidasAlAlta) && tenant.parecidasAlAlta.length > 0 && (
              <p
                role="note"
                className="mt-1 max-w-xl rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs"
              >
                Al registrarse por la invitación, esta empresa se parecía a:{' '}
                {tenant.parecidasAlAlta
                  .map((p: { name: string; motivos: string[] }) => p.name)
                  .join(', ')}
                . Revisá si es la misma empresa.
              </p>
            )}
            {tenant.personas && (
              <TopeDePersonas
                ocupadas={tenant.personas.ocupadas}
                tope={tenant.personas.tope}
                onGuardar={async (maxUsers) => {
                  try {
                    await adminService.fijarTopeDePersonas(tenant.id, maxUsers);
                    await recargarEmpresa();
                    toast({ title: 'Tope de personas actualizado.' });
                  } catch (err: any) {
                    toast({
                      title: 'No se pudo cambiar el tope',
                      description: err?.response?.data?.error ?? err?.message,
                      variant: 'destructive',
                    });
                    throw err;
                  }
                }}
              />
            )}
            <AvisoDeRestauracion personas={sinRestaurar} />
            {tenant.archivedAt && (
              <Badge variant="secondary" className="mt-1">
                Archivada el {new Date(tenant.archivedAt).toLocaleDateString('es-BO')}
              </Badge>
            )}
            {/*
              El rubro va en la cabecera y no en una tarjeta más abajo porque es
              identidad de la empresa, no configuración: responde "¿esta empresa contra
              qué se compara?", que se pregunta antes que cuánto paga.

              Y se puede corregir, que era el hueco: se elegía al crear la empresa y
              después no había forma de cambiarlo desde ninguna pantalla. El rubro de un
              cliente se aclara con el tiempo — el primer día se sabe menos que al mes.
            */}
            <RubroDeLaEmpresa
              rubro={tenant.type}
              onCambiar={cambiarRubro}
              guardando={guardandoRubro}
              soloLectura={!!tenant.archivedAt}
            />
            {/* Demo que vence (C, 2-nov): cuándo vence la prueba y «Extender prueba». */}
            {tenant.esDemo && tenant.demo?.vencimiento && (
              <PlazoDeLaPrueba
                empresaId={tenant.id}
                nombre={tenant.name}
                vencimiento={tenant.demo.vencimiento}
                archivada={!!tenant.archivedAt}
                onExtendida={() => void recargarEmpresa()}
              />
            )}
            {/* Una archivada no se reinicia ni cambia de tope desde acá (409): primero se restaura. */}
            {tenant.esDemo && !tenant.archivedAt && (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <Badge variant="secondary">Demo</Badge>
                <span className="text-sm text-muted-foreground">
                  Entró por una invitación: lo que no compró se ve con candado y las estrategias
                  alcanzan sólo a los productos de ejemplo.
                </span>
                <label className="flex items-center gap-2 text-sm">
                  Productos propios hasta
                  <Input
                    type="number"
                    min={0}
                    step={1}
                    className="h-8 w-24"
                    value={topeDemo ?? String(tenant.topeCatalogoDemo ?? 100)}
                    onChange={(e) => setTopeDemo(e.target.value)}
                    aria-label="Tope de productos propios de la demo"
                  />
                </label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={guardarTopeDemo}
                  disabled={topeDemo === null}
                >
                  Guardar tope
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setReiniciandoDemo(true)}
                  className="border-destructive/50 text-destructive hover:bg-destructive/10"
                >
                  <RotateCcw className="h-4 w-4 mr-2" /> Reiniciar demo
                </Button>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setAssignDialog(true)}>
              <CreditCard className="h-4 w-4 mr-2" /> Asignar Plan
            </Button>
            {/* Una archivada no se convierte desde acá: primero se restaura. */}
            {tenant.esDemo && !tenant.archivedAt && (
              <Button
                variant="outline"
                onClick={() => setConvirtiendo(true)}
                aria-label={`${TEXTOS_DE_LA_CONVERSION.boton}: ${tenant.name}`}
              >
                {TEXTOS_DE_LA_CONVERSION.boton}
              </Button>
            )}
            {tenant.archivedAt ? (
              <Button
                variant="default"
                onClick={handleRestaurar}
                aria-label={`Restaurar ${tenant.name}`}
              >
                <CheckCircle className="h-4 w-4 mr-2" /> Restaurar
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={handleArchivar}
                aria-label={`Archivar ${tenant.name}`}
              >
                <Ban className="h-4 w-4 mr-2" /> Archivar
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => setEliminandoEmpresa(true)}
              className="border-destructive/50 text-destructive hover:bg-destructive/10"
              aria-label={`Eliminar ${tenant.name}`}
            >
              <Trash2 className="h-4 w-4 mr-2" /> Eliminar
            </Button>
            <Button
              variant={tenant.isActive ? 'destructive' : 'default'}
              onClick={handleToggleActive}
              // Nombra la empresa: un lector de pantalla que anuncia solo
              // "Desactivar" no dice a QUIÉN, y es la acción más destructiva
              // de esta pantalla.
              aria-label={`${tenant.isActive ? 'Desactivar' : 'Activar'} ${tenant.name}`}
            >
              {tenant.isActive ? (
                <>
                  <Ban className="h-4 w-4 mr-2" /> Desactivar
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" /> Activar
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { icon: Users, label: 'Usuarios', value: tenant._count?.users || 0 },
            { icon: FileText, label: 'Cotizaciones', value: tenant._count?.quotes || 0 },
            { icon: Layers, label: 'Modelos', value: tenant._count?.pricingModels || 0 },
            { icon: Package, label: 'Materiales', value: tenant._count?.materials || 0 },
            { icon: Building2, label: 'Organizaciones', value: tenant._count?.organizations || 0 },
          ].map((stat) => (
            <Card key={stat.label}>
              <CardContent className="p-4 flex items-center gap-3">
                <stat.icon className="h-5 w-5 text-muted-foreground" />
                <div>
                  <div className="text-2xl font-bold">{stat.value}</div>
                  <div className="text-xs text-muted-foreground">{stat.label}</div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Active Subscription */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Suscripción Actual</CardTitle>
          </CardHeader>
          <CardContent>
            {activeSub ? (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Badge>{activeSub.plan.name}</Badge>
                  <div className="text-sm">
                    <span className="text-muted-foreground">Desde: </span>
                    {new Date(activeSub.startDate).toLocaleDateString()}
                  </div>
                  <div className="text-sm">
                    <span className="text-muted-foreground">Hasta: </span>
                    <span
                      className={
                        new Date(activeSub.endDate) < new Date(Date.now() + 7 * 86400000)
                          ? 'text-amber-500 font-medium'
                          : ''
                      }
                    >
                      {new Date(activeSub.endDate).toLocaleDateString()}
                    </span>
                  </div>
                  {activeSub.isTrial && <Badge variant="secondary">Trial</Badge>}
                </div>
              </div>
            ) : (
              <p className="text-muted-foreground">Sin suscripción activa</p>
            )}
          </CardContent>
        </Card>

        {/* Módulos que compró esta empresa (el "techo") */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Módulos incluidos</CardTitle>
            <p className="text-sm text-muted-foreground">
              Qué puede ver esta empresa. El plan trae una base; acá se ajusta por excepción para
              esta empresa en particular, sin cambiarle el menú a los demás clientes del mismo plan.
            </p>
          </CardHeader>
          <CardContent>
            {id && <TenantModulesPanel key={versionDeModulos} tenantId={id} />}
          </CardContent>
        </Card>

        {/* Y la otra capa: qué hace cada rol con lo que la empresa tiene. */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Permisos por rol</CardTitle>
            <p className="text-sm text-muted-foreground">
              Cómo esta empresa reparte entre sus roles lo que tiene contratado. Se puede ajustar
              desde acá —antes había que entrar como uno de sus usuarios— y queda registrado quién
              lo hizo.
            </p>
          </CardHeader>
          <CardContent>{id && <TenantPermissionsPanel tenantId={id} />}</CardContent>
        </Card>

        {/* Reglas de la demo (343, tanda 4): una empresa en prueba compara contra una sola tienda. */}
        {tenant.esDemo && id && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{TEXTOS_DE_LA_FICHA.titulo}</CardTitle>
              <p className="text-sm text-muted-foreground">{TEXTOS_DE_LA_FICHA.descripcion}</p>
            </CardHeader>
            <CardContent>
              <RubroYTiendaDeLaPrueba
                tenantId={id}
                nombre={tenant.name}
                onCambio={() => setVersionDeTiendas((v) => v + 1)}
              />
            </CardContent>
          </Card>
        )}

        {/* Spec 288 C2 (plan 297): la tercera capa del contrato — qué tiendas de Prixia ve. */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Tiendas disponibles</CardTitle>
            <p className="text-sm text-muted-foreground">
              Qué tiendas del catálogo de Prixia ve esta empresa. Lo leído de cada tienda es de
              Prixia: quitarle una tienda no borra nada; deja de recibir sus lecturas y sale de sus
              pantallas y análisis hasta que se la vuelva a habilitar.
            </p>
          </CardHeader>
          <CardContent>
            {id && (
              <TenantTiendasPrixiaPanel
                key={versionDeTiendas}
                tenantId={id}
                soloLectura={!!tenant.archivedAt}
              />
            )}
          </CardContent>
        </Card>

        {/* Subscription History */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Historial de Suscripciones</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Plan</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Inicio</TableHead>
                  <TableHead>Fin</TableHead>
                  <TableHead>Notas</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tenant.subscriptions?.map((sub: any) => {
                  const config = SUB_STATUS_LABELS[sub.status] || {
                    label: sub.status,
                    variant: 'outline' as const,
                  };
                  return (
                    <TableRow key={sub.id}>
                      <TableCell>
                        <Badge variant="outline">{sub.plan?.name}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={config.variant}>{config.label}</Badge>
                      </TableCell>
                      <TableCell>{new Date(sub.startDate).toLocaleDateString()}</TableCell>
                      <TableCell>{new Date(sub.endDate).toLocaleDateString()}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {sub.notes || '-'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Users */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
            <div>
              <CardTitle className="text-lg">Usuarios ({tenant._count?.users ?? 0})</CardTitle>
              {(tenant.users?.length ?? 0) > (tenant._count?.users ?? 0) && (
                <p className="mt-1 text-xs text-muted-foreground">
                  La lista incluye {(tenant.users?.length ?? 0) - (tenant._count?.users ?? 0)}{' '}
                  persona(s) que no ocupan lugar (desactivadas o de la plataforma).
                </p>
              )}
            </div>
            {/*
              Antes, para darle de alta a alguien del equipo de un cliente había
              que ENTRAR COMO el administrador de ese cliente. Quedaba creado a su
              nombre y sin rastro de que lo hicimos nosotros.
            */}
            <Button size="sm" onClick={() => setNuevoUsuario(true)}>
              <UserPlus className="mr-2 h-4 w-4" aria-hidden="true" />
              Nuevo usuario
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Rol</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Expira</TableHead>
                  <TableHead>Último acceso</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tenant.users?.map((user: any) => (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">
                      {user.firstName} {user.lastName}
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Badge variant={user.role === 'PLATFORM_ADMIN' ? 'destructive' : 'outline'}>
                        {ROLE_LABELS[user.role] || user.role}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {user.isActive ? (
                        <span className="text-green-500 text-sm">Activo</span>
                      ) : (
                        <span className="text-red-400 text-sm">Inactivo</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {user.expiresAt ? (
                        <span
                          className={new Date(user.expiresAt) < new Date() ? 'text-red-400' : ''}
                        >
                          {new Date(user.expiresAt).toLocaleDateString()}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString() : 'Nunca'}
                    </TableCell>
                    <TableCell className="text-right">
                      <RowActionsMenu
                        descripcion={`${user.firstName} ${user.lastName}`}
                        acciones={[
                          {
                            label: 'Entrar como este usuario',
                            icon: <LogIn className="h-4 w-4" />,
                            onSelect: () =>
                              handleImpersonateConfirmado(
                                user.id,
                                `${user.firstName} ${user.lastName}`
                              ),
                          },
                          {
                            label: 'Eliminar usuario',
                            icon: <Trash2 className="h-4 w-4" />,
                            destructive: true,
                            separadorAntes: true,
                            onSelect: () =>
                              handleEliminarUsuario(user.id, `${user.firstName} ${user.lastName}`),
                          },
                        ]}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Assign Plan Dialog */}
        <Dialog open={assignDialog} onOpenChange={setAssignDialog}>
          <DialogContent aria-describedby={undefined}>
            <DialogHeader>
              <DialogTitle>Asignar Plan a {tenant.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Plan</label>
                <SelectorDePlan planes={plans} valor={selectedPlan} onCambio={setSelectedPlan} />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Fecha de vencimiento</label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              <Button
                className="w-full"
                onClick={handleAssignPlan}
                disabled={!selectedPlan || !endDate}
              >
                Asignar Plan
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        {tenant && (
          <NuevoUsuarioDialog
            abierto={nuevoUsuario}
            onClose={() => setNuevoUsuario(false)}
            empresa={{ id: tenant.id, name: tenant.name }}
            onCreado={cargarDetalle}
          />
        )}
        <ReiniciarDemoDialog
          empresa={reiniciandoDemo && tenant ? { id: tenant.id, name: tenant.name } : null}
          onClose={() => setReiniciandoDemo(false)}
          onReiniciada={async ({ completa, archivados }) => {
            setReiniciandoDemo(false);
            const productos =
              archivados === 0
                ? 'No había productos propios para archivar'
                : archivados === 1
                  ? 'Se archivó 1 producto propio'
                  : `Se archivaron ${archivados} productos propios`;
            toast(
              completa
                ? {
                    title: `${tenant.name} se reinició.`,
                    description: `${productos} y el recorrido empieza de nuevo. La empresa lo ve al recargar la página.`,
                  }
                : {
                    variant: 'destructive',
                    title: `${tenant.name} se reinició, pero los productos de ejemplo no quedaron completos.`,
                    description: 'Vuelve a reiniciarla para completarlos.',
                  }
            );
            await cargarDetalle();
          }}
        />
        <EliminarEmpresaDialog
          empresa={
            eliminandoEmpresa && tenant
              ? { id: tenant.id, name: tenant.name, slug: tenant.slug }
              : null
          }
          onClose={() => setEliminandoEmpresa(false)}
          onEliminada={() => navigate('/plataforma/empresas')}
        />
      </div>
    </AppLayout>
  );
}
